<script lang="ts">
  import { fade } from 'svelte/transition';
  import { onMount, onDestroy } from 'svelte';
  import { push } from 'svelte-spa-router';
  import { api } from '../../framework/js/api.js';
  import { toast } from '../../framework/js/toast.js';
  import FolderProject from '../../lib/icons/FolderProject.svelte';

  /** When set, wizard updates this assessment and enqueues generation from the last step. */
  export let editAssessmentId: string | undefined = undefined;

  /** Tracks the backend draft uuid once created mid-flow. */
  let workingUuid: string | undefined = undefined;

  type StepKey = 'name' | 'the_decision' | 'the_assumption' | 'the_stakes' | 'the_customer' | 'what_you_know' | 'time_budget';

  type Step = {
    key: StepKey;
    label: string;
    prompt: string;
    hint?: string;
    placeholder: string;
    bubble: string;
  };

  const steps: Step[] = [
    {
      key: 'name',
      label: 'Assessment Name',
      prompt: 'Give this assessment a short, memorable name.',
      placeholder: 'e.g., Skincare market expansion',
      bubble: "I see that you would like to start a new Illuminator assessment. Let's give it a short name",
    },
    {
      key: 'the_decision',
      label: 'The decision',
      prompt: 'What are you trying to decide in the next 3–6 months?',
      placeholder: 'e.g. Whether to launch a new product line targeting a different age group than our current customers.',
      bubble: "Do not edit yourself. Write as you think.",
    },
    {
      key: 'the_assumption',
      label: 'The assumption',
      prompt: 'What do you believe to be true — but have not yet proven with data?',
      hint: 'Assumptions are unproven beliefs. They feel true, but you are not yet sure.',
      placeholder: 'e.g. I believe women 35+ in our region prefer natural skincare — but I have never validated this.',
      bubble: "Keep it honest — this is where the research will help.",
    },
    {
      key: 'the_stakes',
      label: 'The stakes',
      prompt: 'What goes wrong if you get this one wrong?',
      placeholder: 'e.g. We would invest heavily in stock and a campaign that misses the target.',
      bubble: "Spell out the downside so we can weigh it.",
    },
    {
      key: 'the_customer',
      label: 'The customer',
      prompt: 'Who exactly are you making this decision for?',
      placeholder: 'e.g. Small café owners aged 30–50 in metro areas who are time-poor.',
      bubble: "Be specific about who.",
    },
    {
      key: 'what_you_know',
      label: 'What you know',
      prompt: 'What confirmed facts or data points do you already have in hand?',
      hint: 'Known facts are things you can prove — data, sales figures, customer counts, confirmed feedback.',
      placeholder: 'e.g. We know our refund rate is 12% — we have the numbers.',
      bubble: "Only include what you can prove.",
    },
    {
      key: 'time_budget',
      label: 'Time & budget',
      prompt: 'How quickly do you need to move, and what is realistic to spend?',
      placeholder: 'e.g. We need a decision in 6 weeks. We can spend up to $8,000 on research if it saves us from a bad call.',
      bubble: "Last one — then we'll review.",
    },
  ];

  const DRAFT_KEY = 'assessmentDraft';

  let current = 0;
  let submitting = false;
  let bootstrapping = false;
  let answers = emptyAnswers();

  $: draftStorageKey = editAssessmentId ? `assessmentDraft_edit_${editAssessmentId}` : DRAFT_KEY;

  function emptyAnswers(): Record<StepKey, string> {
    return {
      name: '',
      the_decision: '',
      the_assumption: '',
      the_stakes: '',
      the_customer: '',
      what_you_know: '',
      time_budget: '',
    };
  }

  function loadDraftFromStorage(key: string): Record<StepKey, string> {
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw);
        return {
          name: parsed.name || '',
          the_decision: parsed.the_decision || '',
          the_assumption: parsed.the_assumption || '',
          the_stakes: parsed.the_stakes || '',
          the_customer: parsed.the_customer || '',
          what_you_know: parsed.what_you_know || '',
          time_budget: parsed.time_budget || '',
        };
      }
    } catch (_) {}
    return emptyAnswers();
  }

  function persistDraft() {
    try {
      localStorage.setItem(draftStorageKey, JSON.stringify(answers));
    } catch (_) {}
  }

  function clearDraft() {
    try {
      localStorage.removeItem(draftStorageKey);
    } catch (_) {}
  }

  function lastStepKey(uuid: string): string {
    return `assessment_lastStep_${uuid}`;
  }

  function persistLastStep() {
    if (!workingUuid) return;
    try {
      localStorage.setItem(lastStepKey(workingUuid), String(current));
    } catch (_) {}
  }

  function loadLastStep(uuid: string): number {
    try {
      const raw = localStorage.getItem(lastStepKey(uuid));
      const n = raw ? parseInt(raw, 10) : NaN;
      if (Number.isFinite(n) && n >= 0 && n < steps.length) return n;
    } catch (_) {}
    return 0;
  }

  function clearLastStep(uuid: string) {
    try {
      localStorage.removeItem(lastStepKey(uuid));
    } catch (_) {}
  }

  onMount(async () => {
    if (editAssessmentId) {
      workingUuid = editAssessmentId;
      bootstrapping = true;
      try {
        const res: any = await api.getAssessment(editAssessmentId);
        if (!res?.success || !res.data) {
          toast.showNotification(false, res?.message || 'Could not load assessment');
          push('/assessments');
          return;
        }
        const d = res.data;
        const fromApi = {
          name: d.name || d.title || '',
          the_decision: d.the_decision || '',
          the_assumption: d.the_assumption || '',
          the_stakes: d.the_stakes || '',
          the_customer: d.the_customer || '',
          what_you_know: d.what_you_know || '',
          time_budget: d.time_budget || '',
        };
        const fromStorage = loadDraftFromStorage(draftStorageKey);
        const merged = { ...fromApi };
        (Object.keys(fromStorage) as StepKey[]).forEach((k) => {
          if ((fromStorage[k] || '').trim().length > 0) merged[k] = fromStorage[k];
        });
        answers = merged;
        current = loadLastStep(editAssessmentId);
      } finally {
        bootstrapping = false;
      }
    } else {
      answers = loadDraftFromStorage(DRAFT_KEY);
    }
  });

  $: step = steps[current];
  $: value = answers[step.key];
  $: isLast = current === steps.length - 1;
  $: isFirst = current === 0;
  $: canAdvance = (answers[step.key] || '').trim().length > 0;

  function onInput(e: Event) {
    const t = e.target as HTMLInputElement | HTMLTextAreaElement;
    answers[step.key] = t.value;
    persistDraft();
  }

  function goBack() {
    if (isFirst) {
      push(editAssessmentId ? `/assessments/${editAssessmentId}` : '/assessments');
      return;
    }
    current--;
    persistLastStep();
  }

  async function goNext() {
    if (!canAdvance) return;
    if (!isLast) {
      await saveProgress();
      current++;
      persistLastStep();
      return;
    }
    await submit();
  }

  /** Saves the current draft state to the backend without advancing generation. */
  async function saveProgress() {
    const payload = {
      name: answers.name,
      title: answers.name,
      the_decision: answers.the_decision,
      the_assumption: answers.the_assumption,
      the_stakes: answers.the_stakes,
      the_customer: answers.the_customer,
      what_you_know: answers.what_you_know,
      time_budget: answers.time_budget,
    };
    try {
      if (workingUuid) {
        await api.updateAssessment(workingUuid, payload);
      } else {
        const body: any = await api.createAssessment(payload);
        if (body?.success && body.data?.assessment?.uuid) {
          workingUuid = body.data.assessment.uuid;
        }
      }
    } catch (e) {
      console.error('Draft save failed', e);
    }
  }

  async function submit() {
    if (submitting) return;
    submitting = true;
    try {
      const payload = {
        name: answers.name,
        title: answers.name,
        the_decision: answers.the_decision,
        the_assumption: answers.the_assumption,
        the_stakes: answers.the_stakes,
        the_customer: answers.the_customer,
        what_you_know: answers.what_you_know,
        time_budget: answers.time_budget,
      };

      let uuid = workingUuid;
      if (uuid) {
        const body: any = await api.updateAssessment(uuid, payload);
        if (!body?.success) {
          toast.showNotification(false, body?.message || 'Could not save assessment');
          return;
        }
      } else {
        const body: any = await api.createAssessment(payload);
        if (!body?.success || !body.data?.assessment?.uuid) {
          toast.showNotification(false, body?.message || 'Failed to save');
          return;
        }
        uuid = body.data.assessment.uuid;
        workingUuid = uuid;
      }

      if (!uuid) {
        toast.showNotification(false, 'Could not save assessment');
        return;
      }

      try {
        const gen: any = await api.enqueueAssessmentGenerationAsync(uuid);
        if (!gen?.success) {
          toast.showNotification(false, gen?.message || 'Saved, but could not start Illuminator');
        }
      } catch (e) {
        console.error('Failed to enqueue generation', e);
        toast.showNotification(false, 'Saved, but could not start Illuminator');
      }

      clearDraft();
      if (uuid) clearLastStep(uuid);
      push(`/assessments/${uuid}`);
    } finally {
      submitting = false;
    }
  }

  onDestroy(() => {
    // Always wipe the local cache when the wizard closes — drafts live in the backend now.
    clearDraft();
  });

  function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey && step.key === 'name') {
      e.preventDefault();
      goNext();
    }
  }
</script>

<div class="w640 wizard-wrap">
  {#if bootstrapping}
    <div class="wizard-loading">
      <i class="spinner loading icon" aria-hidden="true"></i>
      <span>Loading assessment…</span>
    </div>
  {:else}
  <div class="wizard-header">
    <span class="wizard-eyebrow">
      <i class="lightbulb icon" aria-hidden="true"></i>
      Illuminator Assessment
    </span>
  </div>

  <div class="progress-tracker">
    <div class="progress-info">
      <span class="progress-label">
        Step {current + 1} of {steps.length} · {step.label}
      </span>
    </div>
    <div class="progress-dots">
      {#each steps as _, idx}
        <div class="progress-dot {idx === current ? 'active' : ''} {idx < current ? 'completed' : ''}"></div>
      {/each}
    </div>
  </div>

  <div class="step-area">
    {#key current}
      <div class="step-body" in:fade={{ duration: 180 }}>
        <div class="ui form">
          <div class="field relative">
            <label class="brief-question" for="assessment-field">
              {step.label}
            </label>
            <p class="step-prompt">{step.prompt}</p>
            <p class="step-hint" class:invisible={!step.hint}>{step.hint || ' '}</p>

            {#if step.key === 'name'}
              <input
                id="assessment-field"
                type="text"
                value={value}
                on:input={onInput}
                on:keydown={onKeydown}
                placeholder={step.placeholder}
                maxlength="100"
                class="brief-textarea brief-textarea-step-1 input_bg"
                required
              />
            {:else}
              <textarea
                id="assessment-field"
                value={value}
                on:input={onInput}
                placeholder={step.placeholder}
                rows="5"
                class="brief-textarea input_bg resize-none"
              ></textarea>
            {/if}

            <div class="absoulte brief-bubble bottom-full bubble bubble-top flow-center align-center padding-1">
              <div class="flow-center bubble-content">
                <div>
                  <FolderProject />
                </div>
                <p>{step.bubble}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    {/key}
  </div>

  <div class="nav-row">
    <button
      type="button"
      class="ui circular icon button"
      on:click={goBack}
      aria-label="Back"
    >
      <i class="angle left icon"></i>
    </button>

    <button
      type="button"
      class="ui circular button icon black"
      on:click={goNext}
      disabled={!canAdvance || submitting}
      aria-label={isLast ? 'Submit assessment' : 'Next'}
    >
      {#if isLast}
        {submitting ? 'Working…' : 'Generate Assessment'}
      {:else}
        <i class="angle right icon"></i>
      {/if}
    </button>
  </div>
  {/if}
</div>

<style>
  .wizard-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    min-height: 12rem;
    color: var(--grey-secondary);
    font-size: 0.95rem;
  }

  .wizard-wrap {
    margin: 0 auto;
    padding: 3rem 1.5rem;
    min-height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .wizard-header {
    display: flex;
    justify-content: center;
    margin-bottom: 0.5rem;
  }

  .wizard-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.35rem 0.75rem;
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--accent-yellow-ink, #a67a00);
    background: var(--accent-yellow-soft, #fff4d1);
    border-radius: 999px;
    letter-spacing: 0.02em;
  }

  .progress-tracker {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    margin-bottom: 2rem;
    padding: 1rem 0;
  }

  .progress-label {
    font-size: 0.9375rem;
    font-weight: 500;
    color: var(--grey-secondary);
    letter-spacing: 0.02em;
  }

  .progress-dots {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }

  .progress-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: var(--greyish);
    border: 2px solid var(--border-color);
    transition: background 0.3s ease, border-color 0.3s ease, width 0.3s ease, height 0.3s ease, box-shadow 0.3s ease;
  }

  .progress-dot.completed {
    background: linear-gradient(135deg, #45c496 0%, #3ab085 100%);
    border-color: #45c496;
    box-shadow: 0 2px 4px rgba(69, 196, 150, 0.3);
  }

  .progress-dot.active {
    width: 14px;
    height: 14px;
    background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(5, 108, 242, 0.2);
  }

  /* Fixed slot for step content so the nav row stays at a constant
     vertical position regardless of whether the step has a hint, a
     short input, a tall textarea, etc. */
  .step-area {
    width: 100%;
    min-height: 380px;
    position: relative;
  }

  .step-body {
    position: absolute;
    inset: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
  }

  .step-prompt {
    margin: 0 0 0.35rem 0;
    color: var(--grey-secondary);
    font-size: 0.95rem;
  }

  .step-hint {
    margin: 0 0 0.75rem 0;
    font-size: 0.85rem;
    color: var(--secondary);
    min-height: 1.2em;
  }

  /* Keep the row in flow but visually hidden when there's no hint, so the
     space below the prompt stays identical for every step. */
  .step-hint.invisible {
    visibility: hidden;
  }

  .nav-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.75rem;
    /* Gap below the fixed-height step slot so the arrows clear
       the textarea's visual bottom edge on every step. */
    margin-top: 4.5rem;
  }

  @media (max-width: 768px) {
    .wizard-wrap { padding: 1.5rem 1rem; }
    .progress-tracker { margin-bottom: 1.25rem; }
    .step-area { min-height: 340px; }
  }
</style>
